import Keycloak from "keycloak-js";

// admin-web client lives in the waffy-staff realm (Google Workspace SSO).
// Staff members authenticate via Google; their KC roles are driven by
// Google Group membership via the waffy-google-group-sync-mapper SPI.
// Backend services accept both waffy and waffy-staff issuers simultaneously
// (configured via KC_STAFF_JWKS_URI / spring.security.keycloak.staff-jwk-set-uri).
export const keycloak = new Keycloak({
  url: process.env.REACT_APP_KEYCLOAK_URL || "https://id-dev.waffyapp.com",
  realm: process.env.REACT_APP_KEYCLOAK_REALM || "waffy-staff",
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || "admin-app",
});

/**
 * @deprecated legacy binary admin gate from the waffy (customer) realm — kept
 * only because contract.provider.tsx still reads it as a redundant defensive
 * check (App.tsx already gates on isStaff() before that provider ever
 * mounts). "ADMIN" is still a real authority, but only staff-admin (the
 * iam-admin@ super-admin tier) composites it in — staff-ops/staff-data are
 * team tags only, and the functional staff-admin-finance/ops/settle/viewer
 * sub-roles composite their own authority + ADMIN_VIEWER, not ADMIN. So
 * hasRealmRole(STAFF_ADMIN_ROLE) recognizes staff-admin only. Prefer
 * isStaff()/canAct(), which cover all 5 admin-web roles correctly.
 */
export const STAFF_ADMIN_ROLE = "ADMIN";

// Staff roles emitted by the waffy-staff realm.
// Each role is composite and also emits legacy authority names so backend
// @PreAuthorize checks keep working without modification.
export const STAFF_ROLES = {
  ADMIN: "staff-admin",
  OPS: "staff-ops",
  DATA: "staff-data",
  ENGINEER: "staff-engineer",
  // Functional sub-roles: Google is the source of truth. Each is granted by
  // joining a dedicated group (admin-finance@/admin-ops@/admin-settle@/admin-viewer@/
  // admin-cs@) independently of staff-admin — deliberate, auditable, per-person access.
  // staff-admin-viewer and staff-admin-cs are both standalone, composable view-only
  // permissions (not tied to any team) — either can be layered onto any of the above.
  ADMIN_FINANCE: "staff-admin-finance",
  ADMIN_OPS: "staff-admin-ops",
  ADMIN_SETTLE: "staff-admin-settle",
  ADMIN_VIEWER: "staff-admin-viewer",
  ADMIN_CS: "staff-admin-cs",
} as const;

// Roles that admit a user into admin-web. staff-ops/staff-data/staff-engineer
// are deliberately excluded — a member of those teams who needs admin-web
// access must be added to the matching staff-admin-* group by IT, rather than
// getting it automatically from their team membership.
//
// Kept in sync manually with the identical ADMIN_WEB_ROLES guard in
// waffy-iam's WaffyGoogleGroupSyncMapper#provisionStaffUser (spi/src/main/java/
// com/gotrah/waffy/keycloak/mapper/WaffyGoogleGroupSyncMapper.java). That guard
// exists for the same reason this list does: staff-ops/staff-data/etc. must
// never grant access just because a KC composite role happens to be wired
// that way for an unrelated reason (e.g. Grafana) — the list of admin-web
// roles is deliberately explicit here and there, independent of KC's
// composite-role structure.
const ADMIN_WEB_ROLES = [
  STAFF_ROLES.ADMIN,
  STAFF_ROLES.ADMIN_FINANCE,
  STAFF_ROLES.ADMIN_OPS,
  STAFF_ROLES.ADMIN_SETTLE,
  STAFF_ROLES.ADMIN_VIEWER,
  STAFF_ROLES.ADMIN_CS,
] as const;

export type StaffTier = "admin" | "ops" | "data" | "engineer" | null;

// Must match one of the Valid Redirect URIs registered for admin-app in Keycloak.
const REDIRECT_URI =
  process.env.REACT_APP_KEYCLOAK_REDIRECT_URI || `${window.location.origin}/`;

const isDev = process.env.NODE_ENV !== "production";

if (isDev) {
  keycloak.onAuthSuccess = () =>
    console.log("[KC] onAuthSuccess — token:", keycloak.token?.slice(0, 20));
  keycloak.onAuthError = (e) => console.error("[KC] onAuthError", e);
  keycloak.onAuthLogout = () => console.log("[KC] onAuthLogout");
}

/**
 * Refreshes the token if it's within 30s of expiring, retrying once after a
 * short delay before giving up. A lone failure here is often transient (e.g.
 * this fires right as a backgrounded tab regains focus, before the network
 * is actually ready) rather than a genuinely dead session — every caller
 * below (the proactive onTokenExpired/visibility hooks and the reactive
 * getAccessToken) shares this so a single blip never forces an unnecessary
 * re-login.
 */
const refreshToken = (): Promise<boolean> =>
  keycloak.updateToken(30).catch(async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return keycloak.updateToken(30);
  });

// Proactive refresh (all environments): the moment the access token expires,
// use the refresh token (stored by keycloak-js at login) to mint a new one, so
// the session stays valid between requests instead of only refreshing lazily
// inside getAccessToken. Redirects to login if the refresh token is also dead.
keycloak.onTokenExpired = () => {
  refreshToken().catch(() => keycloak.login());
};

// keycloak-js schedules onTokenExpired via a setTimeout computed relative to
// the token's exp claim — browsers throttle timers in backgrounded tabs, so
// a tab parked for a long stretch can have that timer fire much later than
// the token's actual expiry (or the SSO session's own idle timeout may have
// already lapsed by then). Force an immediate check when the tab regains
// visibility to close that gap, rather than waiting on the throttled timer.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && keycloak.authenticated) {
    refreshToken().catch(() => keycloak.login());
  }
});

/** Call once at boot. Redirects to Keycloak if not authenticated. */
export const initOidc = (): Promise<boolean> => {
  if (isDev) console.log("[KC] initOidc called — URL:", window.location.href);
  return keycloak.init({
    onLoad: "login-required",
    pkceMethod: "S256",
    // checkLoginIframe disabled: cross-origin iframe check causes CORS errors
    // with Keycloak when the app and KC are on different origins.
    checkLoginIframe: false,
    redirectUri: REDIRECT_URI,
    enableLogging: isDev,
    useNonce: true,
  });
};

/** Synchronous token getter. May be up to ~30s stale; prefer getAccessToken for API calls. */
export const getAccessTokenSync = (): string | null =>
  keycloak.authenticated && keycloak.token ? keycloak.token : null;

/** Async token getter — refreshes if expiring within 30s, redirects to login on failure. */
export const getAccessToken = async (): Promise<string | null> => {
  if (!keycloak.authenticated) return null;
  try {
    await refreshToken();
  } catch {
    keycloak.login();
    return null;
  }
  return keycloak.token ?? null;
};

type TokenClaims = Record<string, unknown> | undefined;

/**
 * Staff email from the KC token (Google identity). Secondary/display field —
 * getUserId() is the primary identity for staff, exactly like every other
 * admin (a real legacy accounts.id, auto-provisioned on first Google login —
 * see auth-service's /internal/staff/provision). Harmless to keep reading
 * regardless of which identity model is authoritative.
 */
export const getEmail = (): string | null => {
  const email =
    (keycloak.idTokenParsed as TokenClaims)?.email ??
    (keycloak.tokenParsed as TokenClaims)?.email;
  return typeof email === "string" ? email : null;
};

/**
 * Legacy Waffy DB user ID claim. Present for both customer-realm tokens and
 * waffy-staff tokens: staff are auto-provisioned a real accounts.id on first
 * Google login (auth-service /internal/staff/provision), and the resulting id
 * is projected into this same claim via a KC user-attribute mapper on the
 * admin-app client — so this reads correctly for staff with no special-casing.
 */
export const getUserId = (): string | null => {
  const id =
    (keycloak.idTokenParsed as TokenClaims)?.userId ??
    (keycloak.tokenParsed as TokenClaims)?.userId;
  if (typeof id === "string") return id;
  if (typeof id === "number") return String(id);
  return null;
};

/**
 * @deprecated National ID / iqama claim — only ever present on customer-realm
 * (waffy) tokens. Staff tokens (waffy-staff) never carry it, so this returns
 * null for every staff session. Kept only because contract.provider.tsx and
 * ProposeSettlementModal still call it; not part of the staff identity model.
 */
export const getUserIdentification = (): string | null => {
  const val =
    (keycloak.idTokenParsed as TokenClaims)?.userIdentification ??
    (keycloak.tokenParsed as TokenClaims)?.userIdentification;
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  return null;
};

/**
 * @deprecated Phone number claim — only ever present on customer-realm
 * (waffy) tokens via a KC user attribute. Staff tokens never carry it, so
 * this returns null for every staff session. Kept only because home.tsx
 * still calls it.
 */
export const getPhoneNumber = (): string | null => {
  const phone = (keycloak.tokenParsed as TokenClaims)?.phoneNumber;
  return typeof phone === "string" ? phone : null;
};

export const getRealmRoles = (): string[] => keycloak.realmAccess?.roles ?? [];

export const hasRealmRole = (role: string): boolean =>
  keycloak.hasRealmRole(role);

/** True if the signed-in user carries any of the 6 admin-web staff roles. */
export const isStaff = (): boolean =>
  ADMIN_WEB_ROLES.some((r) => keycloak.hasRealmRole(r));

/**
 * True if the staff member can perform write/action operations.
 * staff-admin-viewer and staff-admin-cs are both view-only (each composites
 * only into ADMIN_VIEWER) — every other admin-web role can act.
 */
export const canAct = (): boolean =>
  isStaff() &&
  (keycloak.hasRealmRole(STAFF_ROLES.ADMIN) ||
    keycloak.hasRealmRole(STAFF_ROLES.ADMIN_FINANCE) ||
    keycloak.hasRealmRole(STAFF_ROLES.ADMIN_OPS));

/** Returns the most-privileged staff tier the signed-in user belongs to. */
export const getStaffTier = (): StaffTier => {
  if (keycloak.hasRealmRole(STAFF_ROLES.ADMIN)) return "admin";
  if (keycloak.hasRealmRole(STAFF_ROLES.OPS)) return "ops";
  if (keycloak.hasRealmRole(STAFF_ROLES.DATA)) return "data";
  if (keycloak.hasRealmRole(STAFF_ROLES.ENGINEER)) return "engineer";
  return null;
};

export const login = (): Promise<void> => keycloak.login();

export const logout = (): Promise<void> =>
  keycloak.logout({ redirectUri: window.location.origin });
